import { DotsToastMessage } from "dots-ui/dotsToast";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { useContext, useEffect, useState } from "react";
import { ObjectSchema, object } from "yup";
import { AvisConstants } from "../../data/avisConstants";
import { requiredString } from "../../data/yupConstants";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { IPortalAuthenticationModel } from "../../models";
import { ErrorUtils, authApi } from "../../utils";
import { errorDataLoading } from "../shared/appStates/appStates";
import { AvisDescriptiveText } from "../shared/components/form";
import { AvisForm, UnauthorizedLayout } from "../shared/components/index";
import { AvisContext } from "../shared/context/AvisContext";
import { LoginForm } from "./LoginForm";
import { AvisModal } from "../shared/components/modal";

export function Login() {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [isSessionExpired, setIsSessionExpired] = useSessionStorage(
    AvisConstants.session_expired_key,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { login, res } = useContext(AvisContext);

  const validationSchema: ObjectSchema<IPortalAuthenticationModel> = object({
    username: requiredString(res),
    password: requiredString(res),
  });

  useEffect(() => {
    const getParamError = (): string | undefined => {
      const query = new URLSearchParams(location.search);

      const error =
        query.get("error_description") ?? query.get("error") ?? undefined;

      switch (error) {
        case "access_denied":
          return res("error_Auth_eID_AccessDenied");
        default:
          return error;
      }
    };
    setError(getParamError());
    errorDataLoading.value = undefined;
  }, [res]);

  useEffect(() => {
    const shouldShowMessage = isSessionExpired === 1;
    setIsModalOpen(shouldShowMessage);
  }, [isSessionExpired]);

  const onSubmit = (
    values: IPortalAuthenticationModel,
    formikHelper: FormikHelpers<IPortalAuthenticationModel>,
  ) => {
    setError(undefined);
    setSubmitting(true);

    authApi
      .post(
        `Auth/Login`,
        {
          username: values.username,
          password: values.password,
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
          },
        },
      )
      .then((response) => {
        login(response.data);
        formikHelper.resetForm();
      })
      .catch((error) => setError(ErrorUtils.getGeneralErrorMessage(error, res)))
      .finally(() => setSubmitting(false));
  };

  const renderForm = (formikProps: FormikProps<IPortalAuthenticationModel>) => {
    return (
      <LoginForm
        bag={formikProps}
        loading={submitting}
        setError={setError}
        setSubmitting={setSubmitting}
      />
    );
  };

  return (
    <UnauthorizedLayout className="login-form-wrapper">
      {/* hidePageTitle component in this form because it's not in context of a <Router> component*/}
      <AvisForm title={res("login_Form_Title")} hidePageTitle>
        {error && (
          <DotsToastMessage
            className="login-form-toast"
            message={{
              content: error,
              severity: "danger",
              display: "inline",
              variant: "outlined",
            }}
          />
        )}
        <Formik<IPortalAuthenticationModel>
          initialValues={{}}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {renderForm}
        </Formik>

        <AvisModal
          title={res("session_ModalSessionExpiredTitle")}
          isOpen={isModalOpen}
          cancelButtonText={res("button_Close")}
          hideConfirm
          onClose={() => {
            setIsSessionExpired();
            setIsModalOpen(false);
          }}
          modalHeight="auto"
        >
          <div>
            <p>{res("session_ModalSessionExpiredContent")}</p>
          </div>
        </AvisModal>
      </AvisForm>
      <AvisDescriptiveText textKey={"portal_RH_Authentication"} />
    </UnauthorizedLayout>
  );
}
